/**
 * Form.tsx — Orquestrador principal do fluxo de criação de agenda.
 *
 * Fluxo por tipo de atendimento:
 *
 *  Presencial  → [Tipo] → [Localização] → [Serviço] → [Configurações] → [Confirmar]
 *  Tele/Chat   → [Tipo] → [Serviço] → [Configurações] → [Confirmar]
 *               (localização virtual é criada/reutilizada automaticamente)
 *  Domicílio   → [Tipo] → [Serviço] → [Configurações] → [Confirmar]
 *               (localização de domicílio é reutilizada se já existir; caso contrário cria uma nova)
 *
 * Integração FHIR R5:
 *  - Para presencial: usa PostLocation com endereço + PostHealthcareService + PostSchedule
 *  - Para tele/chat: reutiliza location virtual existente ou cria nova com serviceOnline:true
 *  - Para domicílio: reutiliza location existente (attendance-channel=domicilio) ou cria nova
 *    SEM a extension virtual-service (igual ao modelo de tele/chat)
 *  - Todos os schedules recebem extensão attendance-channel para rastreamento do tipo
 */

import { useState } from "react";

import { Stack, Step, StepLabel, Stepper } from "@mui/material";

import { Steps, AttendanceType } from "../../types";

import { Location } from "../Location";
import { HealthCare } from "../HealthCare";
import { ScheduleSettings } from "../ScheduleSettings";
import { ConfirmationScreen } from "../ConfirmationScreen";
import { AttendanceTypeSelector } from "../AttendanceTypeSelector";
import { useSessionContext } from "../../../../contexts/SessionContext";
import {
  PostHealthcareService,
  PostLocation,
  PostSchedule,
} from "../../../../services/fhir";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { oids } from "../../../../configs/Settings";
import { useFormatter } from "../../../../utils/useFormatter";
import { useLocationContext } from "../../../../contexts/LocationContext";
import { useFhirData } from "../../../../utils/useFhirData";

interface IFormInput {
  location: any;
  healthcare: any;
  schedule: any;
}

/**
 * Retorna os labels do Stepper conforme o tipo de atendimento escolhido.
 * Para presencial incluímos o step de Localização; para os demais, pulamos.
 */
function getStepperConfig(attendanceType: AttendanceType | null) {
  if (attendanceType === "presencial") {
    return {
      labels: ["Tipo", "Localização", "Serviço de saúde", "Configurações", "Confirmar"],
      // Mapeamento: índice visual → Steps enum
      stepMap: [
        Steps.S0AttendanceType,
        Steps.S1Location,
        Steps.S2HealthCare,
        Steps.S3TimeAvailable,
        Steps.S4ConfirmationScreen,
      ],
    };
  }
  // Para tele, chat e domicílio: sem step de localização
  return {
    labels: ["Tipo", "Serviço de saúde", "Configurações", "Confirmar"],
    stepMap: [
      Steps.S0AttendanceType,
      Steps.S2HealthCare,
      Steps.S3TimeAvailable,
      Steps.S4ConfirmationScreen,
    ],
  };
}

export function Form() {
  const { access, user } = useSessionContext();
  const { location, refetchLocation } = useLocationContext();
  const navigate = useNavigate();

  const [formData, setFormData] = useState<IFormInput>();
  const [loading, setLoading] = useState(false);
  const [currentStep, setCurrentStep] = useState<Steps>(Steps.S0AttendanceType);

  // Tipo de atendimento selecionado na primeira tela
  const [attendanceType, setAttendanceType] = useState<AttendanceType | null>(null);

  const { labels, stepMap } = getStepperConfig(attendanceType);

  // Índice visual no Stepper (baseado no stepMap)
  const activeStepperIndex = stepMap.indexOf(currentStep);

  /**
   * Resolve a referência de localização para tipos não-presenciais.
   * Tele/Chat: busca location virtual ativa do tipo correto; se não existe, cria uma nova.
   * Domicílio: busca location ativa com attendance-channel=domicilio; se não existe, cria uma nova.
   *   - A location de domicílio NÃO possui a extension virtual-service.
   *   - Uma única location é criada/reutilizada por organização (mesmo comportamento do tele/chat).
   *
   * Retorna { locationId, name } se localização já existe,
   * ou os dados necessários para PostLocation se precisa criar.
   */
  const resolveNonPresentialLocation = () => {
    if (attendanceType === "teleatendimento") {
      // Busca localização virtual ativa de teleatendimento
      const existing = location?.find((loc) => {
        if (loc?.resource?.status !== "active") return false;
        if (!useFhirData.virtualService(loc?.resource)) return false;
        // Verifica se é especificamente teleatendimento (ou sem canal = retrocompatibilidade)
        const channelExt = loc?.resource?.extension?.find(
          (ext: any) =>
            ext.url ===
            "https://fluxmed.com.br/fhir/StructureDefinition/attendance-channel"
        );
        return !channelExt || channelExt.valueCode === "teleatendimento";
      });
      if (existing) {
        // Reutiliza a localização virtual já existente
        return {
          locationId: existing.resource?.id,
          name: existing.resource?.name,
          serviceOnline: true,
          attendanceType: "teleatendimento",
        };
      }
      // Cria nova localização virtual de teleatendimento
      return {
        name: "Teleatendimento",
        serviceOnline: true,
        attendanceType: "teleatendimento",
      };
    }

    if (attendanceType === "chat") {
      // Busca localização virtual ativa de chat
      const existing = location?.find((loc) => {
        if (loc?.resource?.status !== "active") return false;
        if (!useFhirData.virtualService(loc?.resource)) return false;
        const channelExt = loc?.resource?.extension?.find(
          (ext: any) =>
            ext.url ===
            "https://fluxmed.com.br/fhir/StructureDefinition/attendance-channel"
        );
        return channelExt?.valueCode === "chat";
      });
      if (existing) {
        return {
          locationId: existing.resource?.id,
          name: existing.resource?.name,
          serviceOnline: true,
          attendanceType: "chat",
        };
      }
      return {
        name: "Chat",
        serviceOnline: true,
        attendanceType: "chat",
      };
    }

    if (attendanceType === "domicilio") {
      // Busca location ativa de domicílio pelo canal de atendimento.
      // Não usa virtual-service (domicílio não é virtual), apenas attendance-channel.
      const existing = location?.find((loc) => {
        if (loc?.resource?.status !== "active") return false;
        const channelExt = loc?.resource?.extension?.find(
          (ext: any) =>
            ext.url ===
            "https://fluxmed.com.br/fhir/StructureDefinition/attendance-channel"
        );
        return channelExt?.valueCode === "domicilio";
      });
      if (existing) {
        // Reutiliza a location de domicílio já existente (igual ao modelo tele/chat)
        return {
          locationId: existing.resource?.id,
          name: existing.resource?.name,
          serviceOnline: false,
          attendanceType: "domicilio",
        };
      }
      // Cria nova location de domicílio (sem endereço; endereço é adicionado depois)
      return {
        name: "Atendimento a Domicílio",
        serviceOnline: false,
        attendanceType: "domicilio",
      };
    }

    return null;
  };

  const handleStepSubmit = async (data: any) => {
    // Último step: envia todos os dados para o FHIR
    if (currentStep === Steps.S4ConfirmationScreen) {
      setLoading(true);
      try {
        // Determina os dados de localização a usar
        const locationData =
          attendanceType !== "presencial"
            ? resolveNonPresentialLocation()
            : formData?.location;

        const healthcare = await PostHealthcareService(
          formData?.healthcare,
          access.organizationId
        );

        const referenceCalls = {
          locationReference: locationData?.locationId
            ? {
                // Reutiliza localização existente
                id: locationData.locationId,
                name: locationData.name,
              }
            : await PostLocation(
                locationData,
                access.organizationId
              )?.then((response: any) => ({
                id: response?.id,
                name: response?.name,
              })),

          healthcareReference: {
            id: healthcare.id,
            name: healthcare.name,
          },
          // Profissional é escolhido no passo "Serviço de saúde" (HealthCare), não em ScheduleSettings.
          practitionerReference: formData?.healthcare?.professionalActor ||
            formData?.schedule?.professionalActor || {
              label: useFormatter.formatNameDisplay(user.name),
              value: `Practitioner/${oids.cpf}-${user.username}`,
            },
        };

        // Inclui o tipo de atendimento nos dados do schedule para criar a extensão FHIR
        const scheduleData = {
          ...formData?.schedule,
          attendanceType,
        };

        await PostSchedule(scheduleData, referenceCalls);

        refetchLocation();
        toast.success("Agenda criada com sucesso");
        localStorage.setItem("scheduleCreated", "true");

        navigate("/schedule");
      } catch (e) {
        toast.error("Não foi possível criar a agenda, tente novamente!");
      } finally {
        setLoading(false);
      }
    }

    setFormData({ ...formData, ...data });
  };

  /**
   * Avança para o próximo step no stepMap.
   *
   * Aceita `selectedType` para o caso do step 0 (escolha do tipo), onde o
   * estado `attendanceType` ainda não foi atualizado pelo React quando a
   * função é chamada (setState é assíncrono). Assim usamos o valor recém
   * clicado diretamente, sem depender do estado.
   */
  const goToNextStep = (selectedType?: AttendanceType) => {
    // Usa o tipo passado diretamente (step 0) ou o estado atual (demais steps)
    const effectiveType = selectedType ?? attendanceType;
    const { stepMap: currentStepMap } = getStepperConfig(effectiveType);
    const currentIndex = currentStepMap.indexOf(currentStep);
    const nextStep = currentStepMap[currentIndex + 1];
    if (nextStep !== undefined) {
      setCurrentStep(nextStep);
    }
  };

  return (
    <>
      {/* Stepper dinâmico: número de steps varia conforme o tipo de atendimento */}
      <Stack alignItems="center" width="100%">
        <Stepper activeStep={activeStepperIndex} sx={{ width: "80%" }}>
          {labels.map((label, index) => (
            <Step key={index}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>
      </Stack>

      {/* Step 0: Escolha do tipo de atendimento */}
      {currentStep === Steps.S0AttendanceType && (
        <AttendanceTypeSelector
          value={attendanceType}
          onChange={setAttendanceType}
          // goToNextStep recebe o tipo para calcular o stepMap correto imediatamente
          onNext={(type) => goToNextStep(type)}
        />
      )}

      {/* Step 1: Localização (apenas para atendimento presencial) */}
      {currentStep === Steps.S1Location && (
        <Location
          onNext={handleStepSubmit}
          setCurrentStep={(updater: any) => {
            // Garante que navegação por step funcione corretamente dentro do stepMap
            if (typeof updater === "function") {
              const prev = currentStep;
              const next = updater(prev);
              const idx = stepMap.indexOf(prev);
              if (next > prev) {
                // Avançar: vai para o próximo no stepMap
                const nextStep = stepMap[idx + 1];
                if (nextStep !== undefined) setCurrentStep(nextStep);
              } else {
                // Voltar: vai para o anterior no stepMap
                const prevStep = stepMap[idx - 1];
                if (prevStep !== undefined) setCurrentStep(prevStep);
              }
            }
          }}
          defaultValues={formData?.location}
        />
      )}

      {/* Step 2: Serviço de saúde e horários */}
      {currentStep === Steps.S2HealthCare && (
        <HealthCare
          healthcareInfo={formData?.healthcare?.healthService}
          onNext={handleStepSubmit}
          setCurrentStep={(updater: any) => {
            if (typeof updater === "function") {
              const prev = currentStep;
              const next = updater(prev);
              const idx = stepMap.indexOf(prev);
              if (next > prev) {
                const nextStep = stepMap[idx + 1];
                if (nextStep !== undefined) setCurrentStep(nextStep);
              } else {
                const prevStep = stepMap[idx - 1];
                if (prevStep !== undefined) setCurrentStep(prevStep);
              }
            }
          }}
          defaultValues={formData?.healthcare}
        />
      )}

      {/* Step 3: Configurações da agenda */}
      {currentStep === Steps.S3TimeAvailable && (
        <ScheduleSettings
          setCurrentStep={(updater: any) => {
            if (typeof updater === "function") {
              const prev = currentStep;
              const next = updater(prev);
              const idx = stepMap.indexOf(prev);
              if (next > prev) {
                const nextStep = stepMap[idx + 1];
                if (nextStep !== undefined) setCurrentStep(nextStep);
              } else {
                const prevStep = stepMap[idx - 1];
                if (prevStep !== undefined) setCurrentStep(prevStep);
              }
            }
          }}
          onNext={handleStepSubmit}
          defaultValues={formData?.schedule}
        />
      )}

      {/* Step 4: Tela de confirmação */}
      {currentStep === Steps.S4ConfirmationScreen && (
        <ConfirmationScreen
          setCurrentStep={(updater: any) => {
            if (typeof updater === "function") {
              const prev = currentStep;
              const next = updater(prev);
              const idx = stepMap.indexOf(prev);
              if (next > prev) {
                const nextStep = stepMap[idx + 1];
                if (nextStep !== undefined) setCurrentStep(nextStep);
              } else {
                const prevStep = stepMap[idx - 1];
                if (prevStep !== undefined) setCurrentStep(prevStep);
              }
            }
          }}
          onNext={handleStepSubmit}
          loading={loading}
          location={
            // Para presencial usa os dados do form; para outros resolve automaticamente
            attendanceType !== "presencial"
              ? resolveNonPresentialLocation()
              : formData?.location
          }
          healthcare={formData?.healthcare}
          schedule={formData?.schedule}
          attendanceType={attendanceType}
        />
      )}
    </>
  );
}
